/**
 * native-session.js — Embedded playback driven ENTIRELY by the native shell
 * (zap-client-shell): the native QML player owns the video surface, controls,
 * track menus, fullscreen and close. The SPA only resolves the source URL +
 * token, opens it on the shell, and tears down on end/close. NO SPA web-player
 * overlay is mounted — this replaces the old attach-embedded-shell compositing
 * model (transparent stage + mirrored <video> + DOM controls), which kept the
 * whole web player alive behind the native video ("the old broken overlay").
 *
 * Source policy (2026-06-27): RAW byte-range stream for normal containers — the
 * engine opens the ORIGINAL container via zapd's /stream/<sid> (POST /stream/open
 * → GET /stream/<sid>, HTTP byte-range). libav demuxes the real file directly
 * (all audio/subtitle tracks → live select_stream switch, DTS/HEVC, 10-bit HDR,
 * native seek) — NO HLS, NO transcode. zapd is the gate (Privacy::canRead). Disc
 * images go through /disc/open (zapd loop-mounts the .iso + resolves the main
 * .m2ts). A mid-playback source drop is recovered in-place (_recover, bounded).
 * Browser mode never reaches here — openPlayer() routes that to the WASM
 * ZapPlayer (HLS, browser-only).
 *
 * The instance is interface-compatible with ZapPlayer for the views: it exposes
 * destroy() and honours opts.onClose. There is no DOM and no modal.
 */

import { getShellWhenReady } from './shell-bridge.js';
import { isDiscImageSource, nativeInitialSourceMode } from './source-policy.js';
import { discOpen, streamOpen, start } from '../backend.js';
import { preferredAudioLangs639_2 } from '../zap-player.js';
import { toast } from '../../core/toast.js';
import { readResumePos, writeResumePos, clearResumePos } from '../ui/resume.js';

const RESUME_MIN_SEC = 30; // ignore tiny offsets (intro/cold start)
const RESUME_POLL_MS = 5000; // persist position cadence
const TOKEN_REPOINT_MS = 5 * 60 * 1000; // min interval between token re-points
const MAX_RECOVER_RETRIES = 3; // bounded transient-drop re-establish attempts
// Reuse a cached /stream (or /disc) sid only within the server's idle-TTL (5 min,
// StreamHandlers.cpp). A sid reaped after a long pause would 404 on re-open, so a
// cached session older than this is discarded and a fresh one is minted.
const STREAM_SESSION_TTL_MS = 4 * 60 * 1000;
// A line below this cannot carry ANY disc title (DVD ~5-9 Mbit/s, Blu-ray 20-40), so the
// transcoder takes over rather than the picture stalling (2026-09-20).
const DISC_FLOOR_KBPS = 10000;

/** The height a measured line carries. Rates are the server ladder's own targets
 *  (1080/5800, 720/3500, 480/1750, 360/800); the factor is 1.4 rather than the 1.2 used
 *  against a measured file, because a CRF encode peaks above its average and a lossy
 *  line has no room for that -- smooth at 480p beats sharp and stuttering (2026-09-20).
 *  Mirrors ShellWindow.qml's targetHeightFor for the native home. */
function targetHeightFor(wanted, kbps) {
  if (wanted > 0) return wanted;
  if (!(kbps > 0)) return 720;
  if (kbps >= 5800 * 1.4) return 1080;
  if (kbps >= 3500 * 1.4) return 720;
  if (kbps >= 1750 * 1.4) return 480;
  return 360;
}

export class NativeShellSession {
  constructor(opts = {}) {
    this.opts = opts;
    this.path = opts.path || '';
    this.kind = opts.kind || '';
    this.codec = String(opts.codec || '').toLowerCase();
    // Runtime from the row: the probe fills _dur only after the open, far too late to
    // decide whether the original fits through the line (2026-09-20).
    this.durationSec = Number(opts.durationSec) || 0;
    this.getToken = typeof opts.getToken === 'function' ? opts.getToken : () => '';
    this.onTokenChange = typeof opts.onTokenChange === 'function' ? opts.onTokenChange : null;
    this._onClose = typeof opts.onClose === 'function' ? opts.onClose : () => {};

    this._shell = null;
    this._destroyed = false;
    this._closed = false; // onClose fired (or suppressed by destroy)
    this._bufToast = null; // sticky "disc buffering" toast dismiss fn (play-while-download)
    this._unsubs = [];
    this._tokenUnsub = null;
    this._resumeTimer = 0;
    this._sawPlaying = false;
    this._playbackProven = false; // playback reached 'playing' once → recovery armed
    this._recoverRetries = 0; // consecutive transient-drop re-establish attempts
    this._recovering = false; // a re-establish is in flight
    this._activeMode = nativeInitialSourceMode(opts);
    this._discImage = isDiscImageSource(opts);
    this._startSec = 0;
    this._lastPos = 0;
    this._dur = 0;
    this._discSession = null; // {sessionId, streamUrl} — disc image direct-play (m2ts)
    this._streamSession = null; // {sessionId, streamUrl} — normal-file raw byte-range (/stream)
    this._streamSessionAt = 0; // Date.now() when _streamSession was minted (TTL gate)
    this._hlsSession = null; // {playlistUrl} — server HLS transcode (iOS-incompatible codec)
    this._hlsSessionAt = 0; // Date.now() when _hlsSession was minted (TTL gate)
    this._staleRetried = false; // one fresh-sid retry allowed on an initial-open 404
    this._startAbort = null; // AbortController for an in-flight /disc/open or /stream/open

    this._run();
  }

  // ── Source URLs (token-less: the bridge appends the auth token itself and
  //    remembers the clean base for mid-stream re-point) ──────────────────────
  _token() {
    try {
      return this.getToken() || '';
    } catch {
      return '';
    }
  }

  /** DIRECT: an explicit, view-supplied directUrl only. The former fallback
   *  built origin + /media + path here — that route is decommissioned
   *  (zapd answers MEDIA_DECOMMISSIONED; Auflage S0-1, AUFTRAG Stufe 3 M4), so
   *  the fallback now returns '' and _openMode('direct') falls through to the
   *  'stream' session (the path every native open takes anyway, see open()).
   *  The token is NOT appended here — the bridge appends it and remembers the
   *  clean base for mid-stream re-point. */
  _directUrl() {
    const origin = globalThis.location?.origin || '';
    const d = this.opts.directUrl;
    if (d) return d.startsWith('http') ? d : origin + d;
    return '';
  }

  /** Is a STREAM source available at all? Disc images always have one (zapd
   *  /play/* owns DVD/BluRay/ISO handling); a normal file only if the view
   *  pre-resolved a server HLS URL. */
  _hasStream() {
    return this._discImage || !!this.path;
  }

  /** Disc kind for zapd /play/start: pass an explicit dvd/bluray through; for a
   *  plain .iso send NO kind so zapd auto-detects (it has no "iso" kind — ISOs
   *  resolve to dvd/bluray server-side by sniffing BDMV/VIDEO_TS). */
  _discKind() {
    const k = String(this.kind || '').toLowerCase();
    return k === 'dvd' || k === 'bluray' ? k : undefined;
  }

  /** Does this source need a server HLS transcode instead of native DirectPlay?
   *  Apple's Media Engine (all iOS/tvOS devices) HW-decodes ONLY H.264/HEVC (+
   *  AV1 on M3/M4). MPEG-2 (every DVD), VC-1, MPEG-4-ASP, VP9 etc. have NO iOS
   *  HW decoder — DirectPlay falls back to software decode that can't keep up
   *  (a DVD's MPEG-2 over the disc m2ts feed stutters hard, audio underruns).
   *  Route those through /play/start, which transcodes to H.264/AAC the Media
   *  Engine decodes. Desktop (macOS/Windows/Linux) keeps DirectPlay for every
   *  codec — those have broad SW/HW decode and direct is higher quality. */
  _needsTranscode() {
    // GATED OFF (2026-07-10): the server-side HLS transcode GPU pipeline is
    // currently broken for this path — NVDEC decode → `hwdownload` to yuv420p
    // fails ("Invalid output format yuv420p for hwframe download"), so no
    // segments are ever produced and the client bails ("loads then back to
    // home"). This is a pre-existing zapd/HlsOutput bug (predates the routing
    // here), not a client issue. Until it's fixed server-side, keep every
    // native source on the proven DirectPlay path — an MPEG-2 DVD then software-
    // decodes (laggy) but PLAYS, which beats a dead transcode. The routing below
    // is correct and stays in place; flip this guard once the server transcodes.
    if (!globalThis.__zapEnableIosTranscode) return false;
    const plat = String(globalThis.zapShell?.platform || '').toLowerCase();
    if (plat !== 'ios' && plat !== 'tvos') return false;
    // Codecs iOS/tvOS decode in hardware. Anything else → transcode.
    const IOS_HW = /^(h264|avc|avc1|hevc|h265|hvc1|hev1)$/;
    if (this.codec) return !IOS_HW.test(this.codec);
    // No codec hint: a disc image on iOS is almost always a DVD (MPEG-2), the
    // one blind case worth transcoding. A normal file without a hint keeps
    // DirectPlay (H.264/HEVC dominate; direct is better when it works).
    return this._discImage;
  }

  /** STREAM source URL. Disc images (ISO/BluRay) use DIRECT play: zapd
   *  loop-mounts the .iso server-side + resolves the main-title .m2ts, which
   *  the native engine opens DIRECTLY (native decode + byte-range seek, NO
   *  transcode). HLS (/play/*) is browser-only. Normal-file fallback uses the
   *  view's pre-resolved server HLS. Relative URLs (/api-zapd/…) are fine — the
   *  bridge resolves them against the SPA endpoint origin + appends the token. */
  async _resolveStreamUrl() {
    if (!this._discImage) {
      // Native client opens the ORIGINAL container directly via zapd's raw
      // byte-range stream: POST /stream/open -> { stream_url }, GET /stream/<sid>.
      // libav demuxes the real .mkv (all tracks, DTS/HEVC/HDR, native byte-range
      // seek) — NO HLS, NO transcode. ONE URL (no sub-playlists) -> the bridge's
      // ?token authenticates cleanly (nginx ?token->Bearer + ownership). zapd is
      // the gate (Privacy::canRead). HLS (start()) stays browser-only.
      // Reuse the cached sid ONLY within the server's idle-TTL — a sid reaped
      // after a long pause would 404 on re-open, so an older one is dropped and a
      // fresh /stream/open is issued below.
      if (this._streamSession && Date.now() - this._streamSessionAt < STREAM_SESSION_TTL_MS)
        return this._streamSession.streamUrl || '';
      this._streamSession = null;
      this._startAbort = new AbortController();
      // Pass kind through: a torrent:// path needs kind:'torrent' so the server
      // opens the gated play-while-download source (streamOpen has no path-prefix
      // inference like start() does). R1b.next.
      const res = await streamOpen({
        path: this.path,
        kind: this.kind,
        signal: this._startAbort.signal,
      });
      this._startAbort = null;
      this._streamSession = res;
      this._streamSessionAt = Date.now();
      if (this._destroyed) {
        this._stopStreamSession();
        return '';
      }
      return res.streamUrl || '';
    }
    if (this._discSession) return this._discSession.streamUrl;
    this._startAbort = new AbortController();
    const openOpts = { path: this.path, signal: this._startAbort.signal };
    const kind = this._discKind();
    if (kind) openOpts.kind = kind;
    // Play-while-download: while the disc header is still downloading, discOpen
    // retries and pings this with the attempt count; null clears the notice.
    openOpts.onBuffering = (attempt) => {
      if (attempt == null) {
        this._clearBufToast();
      } else if (!this._bufToast && !this._destroyed) {
        this._bufToast = toast.info('Disc wird geladen — Anfang/Ende werden geholt…', {
          sticky: true,
        });
      }
    };
    const res = await discOpen(openOpts);
    this._clearBufToast();
    this._startAbort = null;
    this._discSession = {
      sessionId: res.sessionId,
      streamUrl: res.streamUrl,
      seekIndex: res.seekIndex || null,
      trackLanguages: res.trackLanguages || {},
    };
    // Torn down while /disc/open was in flight → drop the ref (mount persists).
    if (this._destroyed) {
      this._releaseDiscSession();
      return '';
    }
    return res.streamUrl;
  }

  /** HLS TRANSCODE source (iOS/tvOS + a codec the Media Engine can't HW-decode).
   *  POST /play/start transcodes the video to H.264/AAC and returns an m3u8; the
   *  native engine opens it via FileSource::create_remote (libav's hls demuxer)
   *  and HW-decodes the transcoded H.264. No client_caps sent → the server's
   *  legacy transcode-everything path, guaranteeing an iOS-decodable rendition
   *  (a passthrough hint could leave MPEG-2 in the segments, defeating this).
   *  Disc images pass their dvd/bluray kind so zapd mounts + picks the main
   *  title exactly as the browser path does. */
  async _resolveHlsUrl() {
    if (this._hlsSession && Date.now() - this._hlsSessionAt < STREAM_SESSION_TTL_MS)
      return this._hlsSession.playlistUrl || '';
    this._hlsSession = null;
    this._startAbort = new AbortController();
    const startOpts = { path: this.path, signal: this._startAbort.signal };
    const kind = this._discImage ? this._discKind() : this.kind;
    if (kind) startOpts.kind = kind;
    // Ask for a height the line carries BEFORE the session exists -- the server
    // decides copy-vs-encode at start(), so a cap that arrives later changes nothing.
    const wanted = Number(this._qualityPref()) || 0;
    const have = wanted > 0 ? 0 : await this._measureKbps(this._streamSession?.streamUrl || '');
    startOpts.maxHeight = targetHeightFor(wanted, have);
    // All audio tracks in the master, so the shell can apply its remembered language
    // by switching locally -- a single-rendition transcode lost that (2026-09-20).
    startOpts.all_audio = true;
    const langs = preferredAudioLangs639_2();
    if (langs.length) startOpts.audio_lang = langs[0];
    const res = await start(startOpts);
    this._startAbort = null;
    this._hlsSession = res;
    this._hlsSessionAt = Date.now();
    if (this._destroyed) {
      this._stopHlsSession();
      return '';
    }
    // The MASTER, never a variant playlist: the audio rendition lives only in the
    // master, so opening video-vod.m3u8 played silent video (2026-09-20, seen in the
    // server access log -- not one audio segment). With maxHeight the server builds
    // exactly one rung, so there is nothing left to pick here anyway.
    return res.playlistUrl || '';
  }

  // Drop the local HLS-session ref. Like /stream, the server reaps the play
  // session after idle; no explicit close here.
  _stopHlsSession() {
    this._hlsSession = null;
  }

  /** Drop the local disc-session ref. No server DELETE: the loop-mount persists
   *  and is reused by the next open (findExistingIsoMount), like the HLS path. */
  _releaseDiscSession() {
    this._discSession = null;
  }

  /** Dismiss the sticky "disc buffering" toast if one is showing. */
  _clearBufToast() {
    if (this._bufToast) {
      try {
        this._bufToast();
      } catch {
        /* noop */
      }
      this._bufToast = null;
    }
  }

  // Drop the local stream-session ref. The /stream session has no explicit close
  // (zapd reaps it after ~5 min idle), so this just clears the ref — like the disc
  // path's loop-mount that persists for reuse.
  _stopStreamSession() {
    this._streamSession = null;
  }

  async _run() {
    const shell = await getShellWhenReady();
    if (this._destroyed) return;
    if (!shell) {
      // The native client is ALWAYS native — there is no WASM fallback here (the
      // WASM ZapPlayer is browser-only). A missing/incompatible shell bridge is a
      // real error: surface it instead of silently degrading into the web player.
      toast.error('Nativer Player nicht verfügbar');
      this._finishClose();
      return;
    }
    this._shell = shell;
    this._subscribe(shell);

    try {
      // Prefer an explicit resume position from the caller (the native home
      // passes the server-side watch-progress so cross-device resume works);
      // fall back to this device's localStorage position.
      const srv = Number(this.opts.resumeSec);
      const r = Number.isFinite(srv) && srv > RESUME_MIN_SEC ? srv : readResumePos(this.path);
      if (r > RESUME_MIN_SEC) this._startSec = r;
    } catch {
      /* noop */
    }

    // Normal files/discs DirectPlay via /stream|/disc (resolved in
    // _resolveStreamUrl); an iOS-HW-undecodable codec instead transcodes via
    // /play/start (_resolveHlsUrl) so the Media Engine gets an H.264 rendition.
    if (this._needsTranscode()) {
      await this._openMode('hls');
      return;
    }
    await this._openMode((await this._originalFitsLine()) ? 'stream' : 'hls');
  }

  /** Quality preference: 'auto' (default) lets the measurement decide, 'original'
   *  always direct-plays, a number is a height the transcoded ladder is held to.
   *  Stored per device, read fresh so a change applies to the next title. */
  _qualityPref() {
    try {
      return String(localStorage.getItem('zap.videoQuality') || 'auto');
    } catch {
      return 'auto';
    }
  }

  /** Does the ORIGINAL fit through this line right now?
   *
   *  The client used to pull the original container no matter what, and on a thin
   *  line that is not a question of patience: a 2160p source needs more than the
   *  line carries and the picture simply stalls (measured 2026-09-20 at 3 Mbit/s).
   *  So: open the stream session, time a real read of its first bytes, and compare
   *  with what the file itself needs. A fifth of headroom for the dips.
   *
   *  Unknown size or duration -> keep the old behaviour (direct): a guess must not
   *  push a good line onto the transcoder. */
  async _originalFitsLine() {
    const pref = this._qualityPref();
    if (pref === 'original') return true;
    if (pref !== 'auto') return false; // an explicit height means transcode
    try {
      // Discs first: the /disc/open path yields a mounted m2ts/VOB whose size says
      // nothing (the title is a fraction of the image), so the file-bitrate rule cannot
      // apply. What is certain is the floor: a DVD needs roughly 5-9 Mbit/s and a
      // Blu-ray 20-40, so a line under DISC_FLOOR_KBPS cannot carry either and the
      // picture stalls exactly as it did before (Musa 2026-09-20: "geht nicht sieht so
      // aus wie gestern" -- it was a disc). Above it, discs keep direct play.
      const url = await this._resolveStreamUrl();
      if (!url) return true;
      const haveKbps = await this._measureKbps(url);
      if (!haveKbps) return true;
      if (this._discImage) {
        const fits = haveKbps >= DISC_FLOOR_KBPS;
        console.warn(
          `[play] Disc, Leitung ${haveKbps} kbit/s -> ` + (fits ? 'direkt' : 'umwandeln'),
        );
        return fits;
      }
      const size = Number(this._streamSession?.fileSize) || 0;
      const secs = Number(this._dur) || this.durationSec || 0;
      // Unknown bitrate on a MEASURED line is no case for guessing: "healthy line, so
      // direct" sent a 16 Mbit/s file down an 8 Mbit/s line (2026-09-20). Direct only
      // when the original demonstrably fits; without a measurement the old default holds.
      if (size <= 0 || secs <= 0) {
        console.warn(`[play] Dateirate unbekannt, Leitung ${haveKbps} kbit/s -> umwandeln`);
        return false;
      }
      const needKbps = Math.round((size * 8) / secs / 1000);
      const fits = needKbps * 1.2 <= haveKbps;
      console.warn(
        `[play] Original ${needKbps} kbit/s, Leitung ${haveKbps} kbit/s -> ` +
          (fits ? 'direkt' : 'umwandeln'),
      );
      return fits;
    } catch {
      return true;
    }
  }

  /** Time a read of the first bytes of the very stream that would have to flow.
   *  Returns 0 when the read was too short to say anything (round trip dominates). */
  async _measureKbps(streamUrl) {
    try {
      const shell = this._shell;
      const abs = shell?.absoluteUrl ? shell.absoluteUrl(streamUrl) : streamUrl;
      // Count what arrives inside a fixed window instead of waiting for all 2 MB: on a
      // 1 Mbit/s line that wait was 16 s of a tap doing nothing (2026-09-20). How little
      // arrived in 1.2 s IS the line's number.
      const MEASURE_CAP_MS = 1200;
      const ctl = new AbortController();
      const cap = setTimeout(() => ctl.abort(), MEASURE_CAP_MS);
      const t0 = performance.now();
      let got = 0;
      try {
        const r = await fetch(abs, {
          headers: { Range: 'bytes=0-1999999', Authorization: `Bearer ${this._token() || ''}` },
          signal: ctl.signal,
        });
        if (!r.ok && r.status !== 206) return 0;
        const reader = r.body.getReader();
        for (;;) {
          const { done, value } = await reader.read();
          if (done) break;
          got += value.byteLength;
        }
      } catch (e) {
        if (!(e && e.name === 'AbortError')) return 0;
      } finally {
        clearTimeout(cap);
      }
      const ms = performance.now() - t0;
      if (ms < 150 || !got) return 0;
      return Math.round((got * 8) / ms);
    } catch {
      return 0;
    }
  }

  /** Read the master, pick ONE variant. Returns '' when the master has none (then the
   *  caller keeps the master URL, which is still playable). */

  async _openMode(mode) {
    if (this._destroyed) return;
    const shell = this._shell;
    let url = '';
    try {
      url =
        mode === 'direct'
          ? this._directUrl()
          : mode === 'hls'
            ? await this._resolveHlsUrl()
            : await this._resolveStreamUrl();
    } catch {
      /* zapd /stream|/disc|/play/start failed → no source (handled below) */
    }
    if (this._destroyed) return;
    if (!url) {
      if (mode === 'direct' && this._hasStream()) {
        await this._openMode('stream');
        return;
      }
      toast.error('Keine abspielbare Quelle');
      this._finishClose();
      return;
    }
    this._activeMode = mode;
    try {
      const res = await shell.open({
        url,
        path: this.path || undefined,
        kind: this.kind || undefined,
        // Resume is applied as a seek-after-play below: the engine open() does
        // not honour startSec, and seek-after-play is the proven path (a paused
        // pipeline does not render a seek-preview target).
        startSec: 0,
        authToken: this._token() || undefined,
        // Time→byte keyframe index → engine byte-accurate seeks instead of an
        // index-less scan/binary-search. Disc: .clpi EP_map (/disc/open). Torrent
        // stream: mkv Cues / m2ts (/stream/open) so play-while-download seeks land
        // exactly. Absent for mp4 (native moov seek) + complete library files.
        byteIndex:
          (this._discSession && this._discSession.seekIndex) ||
          (this._streamSession && this._streamSession.seekIndex) ||
          undefined,
        trackLanguages: (this._discSession && this._discSession.trackLanguages) || undefined,
      });
      if (this._destroyed) return;
      if (res && res.error) throw new Error(res.error.message || 'open failed');
      // Player surface FIRST: open() is async in the shell ({pending:true} —
      // the engine probe over a cold torrent takes seconds), so the window +
      // "Öffnen…" overlay must be up while it works, not after. play() below
      // is queued by the bridge and applied the moment the open finishes; an
      // open failure arrives as the usual async error event → _onEngineError.
      await shell.setVideoVisible(true);
      await shell.play();
      if (this._destroyed) return;
      if (this._startSec > 0) this._resumeSeekVerified(shell, this._startSec);
      this._wireTokenRefresh();
      this._startResumePoll();
      this._startSeekIndexPoll();
    } catch {
      // Synchronous open()/play() failure → same path as the async error event.
      this._onEngineError();
    }
  }

  /** Resume must LAND, not merely be requested: over a play-while-download
   *  torrent the first seek can fail transiently (target cluster still a hole,
   *  Cues mid-load) — a single fire-and-forget seek then silently left the film
   *  playing from 0:00 ("nach 30s geht er vom Start los"). Verify via
   *  position() and re-issue, bounded. Runs detached from _openMode so playback
   *  start is never delayed; stops on destroy/close/manual-seek-past-target. */
  async _resumeSeekVerified(shell, targetSec) {
    this._verifyPrevPos = undefined; // fresh baseline per verify run (recover re-enters)
    for (let attempt = 0; attempt < 3; attempt++) {
      if (this._destroyed || this._closed) return;
      try {
        await shell.seek(targetSec);
      } catch {
        /* transient — verified below */
      }
      await new Promise((res) => setTimeout(res, 2500));
      if (this._destroyed || this._closed) return;
      try {
        const p = await shell.position();
        // Async open still pending: position() echoes the queued seek target,
        // which would read as "landed" before the real seek even ran. Don't
        // burn an attempt — wait for the open to finish first (bounded: a
        // shell wedged in 'opening' must not spin this loop forever).
        if (p && p.state === 'opening') {
          if ((this._resumeOpenWaits = (this._resumeOpenWaits || 0) + 1) < 40) {
            attempt--;
            continue;
          }
          return;
        }
        const pos = p && typeof p.positionSec === 'number' ? p.positionSec : 0;
        // Landed (or the user meanwhile seeked somewhere themselves) → done.
        if (pos >= targetSec - 45) return;
        // The user grabbed the slider mid-verify: the native controls seek the
        // bridge DIRECTLY (this session never sees it), so a position that
        // JUMPED between our polls is user intent — stop re-issuing the resume
        // target over their scrubbing (live: the loop yanked playback back to
        // the resume point twice). Playback alone can't move 30 s in one tick.
        if (this._verifyPrevPos !== undefined && Math.abs(pos - this._verifyPrevPos) > 30) return;
        this._verifyPrevPos = pos;
      } catch {
        /* bridge hiccup — retry */
      }
    }
  }

  /** T1 async — zapd harvests the torrent's time→byte index in the background
   *  and GET /stream/{sid}/seekindex serves it for a late shell.setByteIndex
   *  injection. DISABLED for now (live 2026-07-10): byte-seeking an MKV
   *  desyncs the demuxer — AVSEEK_FLAG_BYTE just repositions the avio, but
   *  EBML is not self-syncing (unlike the disc path's MPEG-TS, whose 188-byte
   *  packets resync anywhere), so the parser read clusters against stale
   *  master-element bounds ("Element ... exceeds containing master element")
   *  and the decoder chewed garbage NALUs for 30 s+ after every seek/resume.
   *  Timestamp seeks route through the mkv Cues (loaded on demand over the
   *  gated stream, tail is primed) and land exactly on cluster starts — the
   *  correct mkv seek. Re-enable only with a demuxer-level byte entry (or for
   *  containers that genuinely lack an index). */
  _startSeekIndexPoll() {}

  _subscribe(shell) {
    const on = (evt, fn) => {
      try {
        const off = shell.on(evt, fn);
        if (typeof off === 'function') this._unsubs.push(off);
      } catch {
        /* noop */
      }
    };
    on('stateChanged', (p) => {
      const st = p && p.state;
      if (st === 'playing') {
        this._sawPlaying = true;
        // Reached playback → a later error is a transient mid-playback drop
        // (recoverable, e.g. a dropped byte-range socket), not an initial
        // "can't open this source". Arm recovery + reset the retry budget.
        this._playbackProven = true;
        this._recoverRetries = 0;
      }
      // The native ✕ does stop() → 'idle'. idle AFTER playback = user closed
      // (pause is 'paused', open is 'idle' BEFORE playing — _sawPlaying guards).
      // Suppressed while recovering: a re-open transitions through 'idle' too.
      else if (st === 'idle' && this._sawPlaying && !this._recovering && !this._destroyed)
        this._finishClose();
    });
    on('ended', () => {
      try {
        clearResumePos(this.path);
      } catch {
        /* noop */
      }
      this._finishClose();
    });
    on('error', () => this._onEngineError());
    on('deviceLost', () => this._onEngineError());
  }

  _onEngineError() {
    if (this._destroyed) return;
    // Transient mid-playback drop: the origin/network closed the byte-range
    // socket (an idle connection across a long pause, a WiFi blip), and the
    // engine can't resume the dead connection → error/no_first_frame. Playback
    // was PROVEN this session → re-establish the SAME source at the last
    // position (bounded retries). Client-side complement to the engine's
    // in-pipeline blip resilience: short stalls recover inside the pipeline; a
    // fully dropped connection surfaces here.
    if (this._playbackProven) {
      this._recover();
      return;
    }
    // Not proven yet, but we opened against a cached stream/disc session → the
    // failure is most likely a reaped sid (idle > server TTL) on a resume/re-open,
    // NOT a genuinely unopenable source. Retry ONCE with a fresh sid (via _recover,
    // which drops the cached session) before surfacing. This is the resume/re-open
    // complement to the _playbackProven mid-drop path (the "download failed rc=-1"
    // wedge on a re-opened torrent).
    if (!this._staleRetried && (this._streamSession || this._discSession || this._hlsSession)) {
      this._staleRetried = true;
      this._recover();
      return;
    }
    // Never reached playback and no cached session to refresh → the initial open
    // failed (a source the embedded engine can't open). NO HLS fallback in the
    // native client: the engine cannot play legacy /api/hls (it spins fetching
    // segments without producing video — the "geht nix mehr" wedge). Surface + close.
    toast.error('Wiedergabe fehlgeschlagen');
    this._finishClose();
  }

  /** Mid-playback source dropped (origin closed an idle byte-range connection
   *  across a long pause, a transient network blip). Re-open the SAME source at
   *  the last position with a FRESH session + token: the cached /stream (or
   *  /disc) sid may have been reaped after idle and a dead socket won't resume,
   *  so _resolveStreamUrl mints a new sid; the open + seek-after-play lands on a
   *  keyframe (clean recovery). Bounded so a genuinely dead origin can't loop. */
  async _recover() {
    if (this._destroyed || this._recovering) return;
    if (this._recoverRetries >= MAX_RECOVER_RETRIES) {
      toast.error('Wiedergabe unterbrochen — bitte neu öffnen');
      this._finishClose();
      return;
    }
    this._recovering = true;
    this._recoverRetries++;
    // Force a fresh source session (the cached sid/socket is stale after a drop).
    this._stopStreamSession();
    this._releaseDiscSession();
    this._stopHlsSession();
    // Resume where we were; _openMode opens at 0 then seeks to _startSec.
    this._startSec = this._lastPos > RESUME_MIN_SEC ? this._lastPos : 0;
    // The re-open transitions through 'idle' before 'playing'; the _recovering
    // guard suppresses _finishClose, and 'playing' re-arms proven + retry budget.
    this._sawPlaying = false;
    toast.info('Verbindung verloren — wird neu aufgebaut…');
    try {
      await this._openMode(this._activeMode);
    } finally {
      this._recovering = false;
    }
  }

  _startResumePoll() {
    if (this._resumeTimer || this._destroyed) return;
    this._resumeTimer = setInterval(async () => {
      if (this._destroyed || !this._shell) return;
      try {
        const p = await this._shell.position();
        if (p && typeof p.positionSec === 'number') {
          this._lastPos = p.positionSec;
          if (typeof p.durationSec === 'number' && p.durationSec > 0) this._dur = p.durationSec;
          if (this._lastPos > RESUME_MIN_SEC) writeResumePos(this.path, this._lastPos, this._dur);
        }
      } catch {
        /* noop */
      }
    }, RESUME_POLL_MS);
  }

  _wireTokenRefresh() {
    if (!this.onTokenChange || this._tokenUnsub) return;
    let lastPushed = this._token() || null;
    let lastAt = 0;
    const unsub = this.onTokenChange((tok) => {
      if (!tok || tok === lastPushed || this._destroyed || !this._shell) return;
      const now = Date.now();
      if (now - lastAt < TOKEN_REPOINT_MS) return;
      lastPushed = tok;
      lastAt = now;
      try {
        this._shell.setAuthToken(tok);
      } catch {
        /* noop */
      }
    });
    if (typeof unsub === 'function') {
      this._tokenUnsub = unsub;
      this._unsubs.push(unsub);
    }
  }

  // Engine ended / user closed (native ✕) → persist position, clean up, and let
  // the view drop its reference. The shell already hid the native video (✕ does
  // setVideoVisible(false)), so the SPA library reappears on its own.
  _finishClose() {
    if (this._closed) return;
    this._closed = true;
    try {
      if (this._lastPos > RESUME_MIN_SEC) writeResumePos(this.path, this._lastPos, this._dur);
    } catch {
      /* noop */
    }
    this._teardown();
    try {
      this._onClose();
    } catch {
      /* noop */
    }
  }

  _teardown() {
    this._destroyed = true;
    this._clearBufToast();
    if (this._startAbort) {
      try {
        this._startAbort.abort();
      } catch {
        /* noop */
      }
      this._startAbort = null;
    }
    if (this._seekIdxAbort) {
      try {
        this._seekIdxAbort.abort();
      } catch {
        /* noop */
      }
      this._seekIdxAbort = null;
    }
    if (this._resumeTimer) {
      clearInterval(this._resumeTimer);
      this._resumeTimer = 0;
    }
    for (const off of this._unsubs) {
      try {
        off();
      } catch {
        /* noop */
      }
    }
    this._unsubs = [];
    this._releaseDiscSession();
    this._stopStreamSession();
    this._stopHlsSession();
  }

  // View-initiated teardown (opening another film, leaving the view). Stops the
  // engine + hides the native video. Does NOT fire onClose — the view owns its
  // reference here. Suppress any late event from re-entering onClose.
  destroy() {
    if (this._destroyed && this._closed) return;
    this._closed = true;
    try {
      this._shell?.stop?.();
    } catch {
      /* noop */
    }
    try {
      this._shell?.setVideoVisible?.(false);
    } catch {
      /* noop */
    }
    this._teardown();
  }
}
